import { Chip, Divider, makeStyles, Tooltip, Typography, useTheme } from '@material-ui/core';
import { useQualityTranslation } from '../../../../../helpers/hooks/useQualityTranslation';
import { viewPositionText } from '../../../../../helpers/qualityProjection';
import { useTranslation } from 'react-i18next';
import { qualityClassColor } from '../../../../../helpers/manageColors';

const useStyles = makeStyles((theme) => ({
  // QualityChips styles
  tooltip_title: {
    letterSpacing: '0.1rem',
    fontSize: theme.fonts.responsiveMedium,
    fontWeight: 'bold',
    textTransform: 'capitalize',
  },

  tooltip_explanation: {
    letterSpacing: '0.1rem',
    fontSize: theme.fonts.responsiveMedium,
  },

  tooltip_points: {
    letterSpacing: '0.06rem',
    fontSize: theme.fonts.responsiveMedium,
    fontWeight: 600,
    marginTop: '4px',
  },

  tooltip_divider: {
    margin: '3% 0',
  },

  quality_chip_container: {
    fontSize: theme.fonts.responsiveMedium,
    cursor: 'help',
    height: '100%',
    width: '85%',
    minWidth: '134px',
    borderRadius: '5px',
    padding: '4px 8px',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '30px',
  },

  quality_chip_label: {
    fontSize: '9px',
    position: 'absolute',
    top: '2px',
    left: '4px',
    color: '#bdbdbd',
  },

  quality_chip_text: {
    fontSize: theme.fonts.responsiveMedium,
    letterSpacing: '0.1rem',
    fontWeight: 'bold',
  },

  quality_chip_not_evaluated_container: {
    background: 'transparent',
    border: '1px solid gray',
    fontSize: theme.fonts.responsiveMedium,
    cursor: 'help',
    height: '100%',
    width: '85%',
    borderRadius: '5px',
    padding: '4px 8px',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '20px',
    fontStyle: 'italic',
    minWidth: '134px',
  },

  quality_chip_not_evaluated_text: {
    fontSize: '0.8rem',
    minWidth: '35px',
    color: '#c2c2c2',
  },
}));

export const QualityCustomChip = ({ result, explanation, qualityPoints, projection, projLabel }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const classes = useStyles();
  const hasQualityPoints = Number.isFinite(qualityPoints);

  const translateOrDefault = (key, fallback) => {
    const translated = t(key);
    return translated === key ? fallback : translated;
  };

  if (result) {
    return (
      <Tooltip
        arrow
        placement={projection === 'rcc' || projection === 'rmlo' ? 'left' : 'right'}
        title={
          <>
            <Typography className={classes.tooltip_title}>
              {`${
                projection === 'general'
                  ? t('ResultView.general_quality')
                  : projection
                  ? viewPositionText(projection, t)
                  : '-'
              } ${t('ResultView.quality')}: ${
                t(`Quality.${result}`, { defaultValue: result }) ?? t('ResultView.not_evaluated')
              }`}
            </Typography>
            {hasQualityPoints && (
              <Typography className={classes.tooltip_points}>
                {`${qualityPoints} ${t('Quality.points_label', { defaultValue: t('Dashboard.points') })}`}
              </Typography>
            )}
            {explanation && explanation.length > 0 && (
              <>
                <Divider className={classes.tooltip_divider} />
                {explanation.map((exp, index) => (
                  <Typography key={index} className={classes.tooltip_explanation}>
                    {Array.isArray(exp)
                      ? `- ${exp.map((e) => translateOrDefault(`Quality.${e}`, e)).join(' ')}`
                      : `- ${translateOrDefault(`Quality.${exp}`, exp)}`}
                  </Typography>
                ))}
              </>
            )}
          </>
        }>
        <div
          className={classes.quality_chip_container}
          style={{
            background: result ? `${qualityClassColor(result, theme)}50` : 'transparent',
            color: result ? 'white' : theme.palette.status.notEvaluated,
          }}>
          <Typography className={classes.quality_chip_label}>{projLabel}</Typography>
          <Typography className={classes.quality_chip_text}>
            {useQualityTranslation(result, t)?.toUpperCase() ?? t('ResultView.is_not_evaluated')}
          </Typography>
        </div>
      </Tooltip>
    );
  }
  return (
    <div
      className={classes.quality_chip_not_evaluated_container}
      style={{
        color: theme.palette.status.notEvaluated,
      }}>
      <Typography className={classes.quality_chip_not_evaluated_text}>
        {t('ResultView.is_not_evaluated')}
      </Typography>
    </div>
  );
};
